import { Link } from "react-router-dom";
import { useMemo, useState } from "react";
import { normalizeSalaryPayoutMode, PAYOUT_FULL_PAY } from "../../../Salary/SalaryPayoutModeField";
import {
  Activity,
  Award,
  BadgeCheck,
  Banknote,
  BriefcaseBusiness,
  Building2,
  CalendarCheck,
  CalendarCheck2,
  CalendarClock,
  CalendarDays,
  CalendarX,
  ChevronRight,
  ClipboardList,
  Clock3,
  CreditCard,
  FileClock,
  FileText,
  FolderOpen,
  Fingerprint,
  GitBranch,
  HandCoins,
  Hash,
  History,
  IdCard,
  IndianRupee,
  KeyRound,
  Landmark,
  Laptop,
  Layers,
  LayoutDashboard,
  MapPin,
  MapPinned,
  Network,
  Palmtree,
  Pencil,
  Percent,
  ReceiptText,
  Repeat,
  Route,
  Scale,
  Settings2,
  ShieldCheck,
  Tags,
  Timer,
  TrendingDown,
  TrendingUp,
  UserCheck,
  UserRound,
  Users,
  Wallet,
} from "lucide-react";
import {
  ProfileDetailTable,
  ProfileEmptyState,
  ProfileErrorState,
  ProfileField,
  ProfileFieldGrid,
  ProfilePanel,
  ProfileSkeleton,
  ProfileTableWrap,
} from "../components/ProfileSection";
import {
  buildLifecycleEvents,
  departmentLabel,
  designationLabel,
  employeeDisplayName,
  formatCurrencyInr,
  formatDays,
  formatProfileDate,
  formatStatusLabel,
  locationLabel,
  leaveBalanceEntitled,
  leaveBalanceRemaining,
  leaveBalanceUsed,
  managerLabel,
  maskSensitive,
} from "../employeeProfileUtils";
import {
  canEditEmployee,
  canManageSalary,
  canViewStatutorySensitive,
} from "../employeeProfilePermissions";
import AdvanceTakenSection from "../components/AdvanceTakenSection";
import { getLifecycleDocumentName, getLifecycleDocumentUrl } from "../../../Lifecycle/lifecycleApi";
import FuelReimbursementSection, { mediaUrl } from "../components/FuelReimbursementSection";
import EmployeeSummaryCards from "../components/EmployeeSummaryCards";

function SectionState({ section, emptyTitle, emptyDesc, children, onRetry }) {
  if (section.loading && !section.loaded) return <ProfileSkeleton rows={5} />;
  if (section.error === "forbidden") {
    return (
      <ProfileEmptyState
        title="Restricted"
        description="You do not have permission to view this section."
      />
    );
  }
  if (section.error) {
    return <ProfileErrorState message="Unable to load this section." onRetry={onRetry} />;
  }
  if (section.loaded && !section.data && emptyTitle) {
    return <ProfileEmptyState title={emptyTitle} description={emptyDesc} />;
  }
  return children;
}

function OverviewTab({ employee, sections, role, onEdit, onGoTab }) {
  const salarySec = sections.get("salary");
  const attSec = sections.get("attendanceSummary");
  const leaveSec = sections.get("leaveSummary");

  const structure = salarySec.data?.structure;
  const calc = salarySec.data?.calculation;
  const meta = employee?.metadata || employee?.extra_metadata || {};
  const att = attSec.data?.summary;
  const balances = leaveSec.data?.balances || [];

  const detailRows = [
    { label: "Full Name", value: employeeDisplayName(employee) },
    { label: "Employee Code", value: employee?.employee_code, mono: true },
    { label: "Date of Birth", value: formatProfileDate(employee?.date_of_birth) },
    { label: "Gender", value: employee?.gender },
    { label: "Personal Email", value: employee?.email },
    { label: "Work Email", value: employee?.work_email || employee?.email },
    { label: "Phone", value: employee?.mobile_phone || employee?.phone },
    { label: "Address", value: meta.current_address },
  ];

  const jobRows = [
    { label: "Department", value: departmentLabel(employee) },
    { label: "Designation", value: designationLabel(employee) },
    { label: "Employment Type", value: employee?.employee_type },
    { label: "Reporting Manager", value: managerLabel(employee) },
    { label: "Location", value: locationLabel(employee) },
    { label: "Shift / Schedule", value: meta.shift },
  ];
  if (employee?.direct_reports_count != null) {
    jobRows.push({ label: "Team Size", value: employee.direct_reports_count });
  }

  const salaryRows = structure
    ? [
        {
          label: "Salary Template",
          value: structure.template_name || structure.salary_template_name || structure.template?.name,
        },
        { label: "Effective From", value: formatProfileDate(structure.effective_from) },
        { label: "CTC", value: formatCurrencyInr(structure.ctc ?? employee?.annual_ctc) },
        { label: "Gross", value: formatCurrencyInr(calc?.gross_salary ?? calc?.gross) },
        { label: "Net", value: formatCurrencyInr(calc?.net_salary ?? calc?.net) },
        { label: "Pay Frequency", value: employee?.pay_frequency || "Monthly" },
      ]
    : [];

  const attendanceRows = att
    ? [
        { label: "Working Days", value: att.workingDays },
        { label: "Present", value: att.present },
        { label: "Absent", value: att.absent },
        { label: "Half Day", value: att.halfDay },
        { label: "Leave", value: att.leave },
        { label: "Attendance %", value: `${att.attendancePct}%` },
      ]
    : [];

  return (
    <div className="ep-tab-stack ep-overview-layout">
      <ProfilePanel
        title="Overview"
        icon={LayoutDashboard}
        subtitle="Quick snapshot of key details and important information."
      >
        <EmployeeSummaryCards employee={employee} sections={sections} />
      </ProfilePanel>

      <ProfilePanel
        title="Employee Details"
        icon={IdCard}
        tone="indigo"
        variant="table"
        actions={
          canEditEmployee(role) ? (
            <button type="button" className="ep-btn ep-btn-ghost ep-btn-sm" onClick={() => onEdit(1)}>
              <Pencil size={13} aria-hidden />
              Edit
            </button>
          ) : null
        }
      >
        <ProfileDetailTable
          pairs={1}
          rows={detailRows}
          caption="Employee details"
          className="ep-kv-wrap--brand"
        />
      </ProfilePanel>

      <ProfilePanel
        title="Job & Organization"
        icon={Network}
        tone="indigo"
        variant="table"
        actions={
          canEditEmployee(role) ? (
            <button type="button" className="ep-btn ep-btn-ghost ep-btn-sm" onClick={() => onEdit(2)}>
              <Pencil size={13} aria-hidden />
              Edit
            </button>
          ) : null
        }
      >
        <ProfileDetailTable pairs={1} rows={jobRows} caption="Job and organization" />
      </ProfilePanel>

      <ProfilePanel
        title="Current Salary / Package"
        icon={Wallet}
        tone="indigo"
        variant="table"
        actions={
          canManageSalary(role) ? (
            <button type="button" className="ep-btn ep-btn-ghost ep-btn-sm" onClick={() => onGoTab("salary")}>
              View Salary Details
            </button>
          ) : null
        }
      >
        <SectionState section={salarySec} emptyTitle="No salary package assigned">
          {structure ? (
            <ProfileDetailTable pairs={1} rows={salaryRows} caption="Current salary package" />
          ) : (
            <ProfileEmptyState
              title="No active salary package"
              description="Assign a salary template from Salary & Packages or Employee Salary Structure."
              action={
                canManageSalary(role) ? (
                  <Link className="ep-btn ep-btn-secondary" to={`/employeesalarystructure?employee_id=${employee?.employee_id}`}>
                    Assign package
                  </Link>
                ) : null
              }
            />
          )}
        </SectionState>
      </ProfilePanel>

      <ProfilePanel
        title="Attendance Summary"
        subtitle="Current month"
        icon={CalendarCheck}
        tone="indigo"
        variant="table"
        actions={
          <button type="button" className="ep-btn ep-btn-ghost ep-btn-sm" onClick={() => onGoTab("attendance")}>
            View Attendance
          </button>
        }
      >
        <SectionState section={attSec} emptyTitle="No attendance this month">
          {att ? <ProfileDetailTable pairs={1} rows={attendanceRows} caption="Attendance this month" /> : null}
        </SectionState>
      </ProfilePanel>

      <ProfilePanel
        title="Leave Balances"
        subtitle={`Year ${new Date().getFullYear()}`}
        icon={Palmtree}
        tone="indigo"
        variant="table"
        actions={
          <button type="button" className="ep-btn ep-btn-ghost ep-btn-sm" onClick={() => onGoTab("leave")}>
            View Leave
          </button>
        }
      >
        <SectionState section={leaveSec} emptyTitle="No leave balances">
          {balances.length ? (
            <ProfileTableWrap>
              <table className="ep-table">
                <thead>
                  <tr>
                    <th>Leave Type</th>
                    <th>Entitled</th>
                    <th>Used</th>
                    <th>Available</th>
                  </tr>
                </thead>
                <tbody>
                  {balances.map((b) => (
                    <tr key={b.balance_id || b.leave_type_id || b.leave_type_name}>
                      <td>{b.leave_type_name || b.name || "—"}</td>
                      <td>{formatDays(leaveBalanceEntitled(b))}</td>
                      <td>{formatDays(leaveBalanceUsed(b))}</td>
                      <td>{formatDays(leaveBalanceRemaining(b))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ProfileTableWrap>
          ) : (
            <ProfileEmptyState title="Leave not configured" description="Leave balances will appear when policies are allocated." />
          )}
        </SectionState>
      </ProfilePanel>

      <FuelReimbursementSection employeeId={employee?.employee_id} role={role} hideWhenDisabled />

      <ProfilePanel
        title="Recent Activity"
        icon={History}
        subtitle="Derived from employee record timestamps — full audit history is on the Activity tab."
      >
        <ul className="ep-timeline">
          {buildLifecycleEvents(employee).slice(-5).reverse().map((ev) => (
            <li key={ev.id}>
              <span className="ep-timeline-date">{formatProfileDate(ev.date)}</span>
              <strong>{ev.title}</strong>
              <span>{ev.description}</span>
            </li>
          ))}
        </ul>
      </ProfilePanel>
    </div>
  );
}

function PersonalTab({ employee, role, onEdit }) {
  const meta = employee?.metadata || employee?.extra_metadata || {};
  const editAction = canEditEmployee(role) ? (
    <button type="button" className="ep-btn ep-btn-ghost ep-btn-sm" onClick={() => onEdit(1)}>
      <Pencil size={13} aria-hidden />
      Edit
    </button>
  ) : null;

  const personalRows = [
    { label: "Full Name", value: employeeDisplayName(employee) },
    { label: "First Name", value: employee?.first_name },
    { label: "Middle Name", value: employee?.middle_name },
    { label: "Last Name", value: employee?.last_name },
    { label: "Date of Birth", value: formatProfileDate(employee?.date_of_birth) },
    { label: "Gender", value: employee?.gender },
    { label: "Marital Status", value: employee?.marital_status },
    { label: "Personal Email", value: employee?.email },
    { label: "Work Email", value: employee?.work_email },
    { label: "Mobile", value: employee?.mobile_phone || employee?.phone },
    { label: "Alternate Phone", value: meta.alternate_phone },
  ];
  // Address and Emergency Contact panels are hidden for now (see below).
  // const addressRows = [
  //   { label: "Current Address", value: meta.current_address },
  //   { label: "Permanent Address", value: meta.permanent_address },
  // ];
  // const emergencyRows = [
  //   { label: "Name", value: meta.emergency_contact_name },
  //   { label: "Relationship", value: meta.emergency_contact_relationship },
  //   { label: "Phone", value: meta.emergency_contact_phone },
  //   { label: "Email", value: meta.emergency_contact_email },
  // ];

  return (
    <div className="ep-tab-stack">
      <ProfilePanel title="Personal Information" icon={UserRound} tone="indigo" variant="table" actions={editAction}>
        <ProfileDetailTable pairs={1} rows={personalRows} caption="Personal information" />
      </ProfilePanel>
      {/* Address and Emergency Contact — hidden for now.
      <ProfilePanel title="Address" icon={MapPin} tone="indigo" variant="table" actions={editAction}>
        <ProfileDetailTable pairs={1} rows={addressRows} caption="Address" />
      </ProfilePanel>
      <ProfilePanel title="Emergency Contact" icon={PhoneCall} tone="indigo" variant="table" actions={editAction}>
        <ProfileDetailTable pairs={1} rows={emergencyRows} caption="Emergency contact" />
      </ProfilePanel>
      */}
    </div>
  );
}

function EmploymentTab({ employee }) {
  const meta = employee?.metadata || employee?.extra_metadata || {};
  const history = meta.employment_history;
  const events = Array.isArray(history) ? history : buildLifecycleEvents(employee);

  return (
    <div className="ep-tab-stack">
      <ProfilePanel title="Job Details" icon={BriefcaseBusiness} tone="indigo">
        <ProfileFieldGrid columns={4}>
          <ProfileField icon={Hash} tone="indigo" label="Employee Code" value={employee?.employee_code} mono />
          <ProfileField icon={Tags} tone="slate" label="Employee Category" value={employee?.role} />
          <ProfileField icon={BriefcaseBusiness} tone="violet" label="Employment Type" value={employee?.employee_type} />
          <ProfileField icon={BadgeCheck} tone="green" label="Status" value={formatStatusLabel(employee?.status)} />
          <ProfileField icon={CalendarDays} tone="blue" label="Joining Date" value={formatProfileDate(employee?.date_of_joining)} />
          <ProfileField icon={CalendarCheck2} tone="teal" label="Confirmation Date" value={formatProfileDate(meta.confirmation_date)} />
          <ProfileField icon={Award} tone="amber" label="Grade" value={meta.grade} />
          <ProfileField icon={Clock3} tone="rose" label="Shift / Schedule" value={meta.shift} />
        </ProfileFieldGrid>
      </ProfilePanel>
      <ProfilePanel title="Organization" icon={Building2} tone="indigo">
        <ProfileFieldGrid columns={3}>
          <ProfileField icon={Building2} tone="blue" label="Department" value={departmentLabel(employee)} />
          <ProfileField icon={IdCard} tone="violet" label="Designation" value={designationLabel(employee)} />
          <ProfileField icon={UserRound} tone="teal" label="Reporting Manager" value={managerLabel(employee)} />
          <ProfileField icon={MapPin} tone="amber" label="Work Location" value={locationLabel(employee)} />
          <ProfileField icon={GitBranch} tone="rose" label="Branch" value={meta.branch} />
          <ProfileField icon={Layers} tone="indigo" label="Business Unit" value={employee?.business_unit} />
        </ProfileFieldGrid>
      </ProfilePanel>
      <ProfilePanel title="Employment History" icon={History} tone="indigo">
        {events.length ? (
          <ul className="ep-timeline">
            {events.map((ev, idx) => (
              <li key={ev.id || idx}>
                <span className="ep-timeline-date">{formatProfileDate(ev.date || ev.effective_date)}</span>
                <strong>{ev.title || ev.event}</strong>
                <span>{ev.description || ev.note || "—"}</span>
              </li>
            ))}
          </ul>
        ) : (
          <ProfileEmptyState
            title="No employment history records"
            description="Promotion, transfer, and role change events will appear here when recorded."
          />
        )}
      </ProfilePanel>
    </div>
  );
}

/** Render booleans stored in metadata as Yes / No (React does not print true/false). */
function yesNo(value) {
  if (value === true || value === "true") return "Yes";
  if (value === false || value === "false") return "No";
  return value ?? "—";
}

function OrganizationTab({ employee, section, onRetry }) {
  const chain = [
    { icon: UserRound, tone: "indigo", title: employeeDisplayName(employee), sub: designationLabel(employee), focus: true },
    {
      icon: UserCheck,
      tone: "teal",
      title: section.data?.manager?.employee_name || managerLabel(employee),
      sub: "Reporting Manager",
    },
    { icon: Building2, tone: "blue", title: departmentLabel(employee), sub: "Department" },
  ];
  if (employee?.business_unit) {
    chain.push({ icon: Layers, tone: "violet", title: employee.business_unit, sub: "Business Unit" });
  }

  return (
    <div className="ep-tab-stack">
      <ProfilePanel
        title="Reporting Structure"
        icon={Network}
        tone="indigo"
        actions={
          <Link className="ep-btn ep-btn-ghost ep-btn-sm" to="/org-hierarchy">
            Open Reporting Hierarchy
          </Link>
        }
      >
        <SectionState section={section} onRetry={onRetry}>
          <ol className="ep-org-chain">
            {chain.map((node, idx) => (
              <li key={node.sub} className="ep-org-step">
                {idx > 0 ? <ChevronRight className="ep-org-arrow" size={18} aria-hidden /> : null}
                <div className={`ep-org-node ep-ftone--${node.tone}${node.focus ? " ep-org-node--focus" : ""}`}>
                  <span className="ep-field-chip" aria-hidden>
                    <node.icon size={17} strokeWidth={2} />
                  </span>
                  <span className="ep-field-text">
                    <strong>{node.title || "—"}</strong>
                    <span>{node.sub}</span>
                  </span>
                </div>
              </li>
            ))}
          </ol>
        </SectionState>
      </ProfilePanel>
      <ProfilePanel title="Direct Reports" icon={Users} tone="indigo" variant="table">
        <SectionState section={section} onRetry={onRetry}>
          {section.data?.directReports?.length ? (
            <ProfileTableWrap>
              <table className="ep-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Code</th>
                    <th>Designation</th>
                  </tr>
                </thead>
                <tbody>
                  {section.data.directReports.map((r) => (
                    <tr key={r.employee_id}>
                      <td>
                        <Link to={`/employees/${r.employee_id}`}>
                          {r.employee_name || r.display_name || "—"}
                        </Link>
                      </td>
                      <td className="ep-mono">{r.employee_code || "—"}</td>
                      <td>{r.designation || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ProfileTableWrap>
          ) : (
            <ProfileEmptyState title="No direct reports" description="This employee has no active direct reports." />
          )}
        </SectionState>
      </ProfilePanel>
    </div>
  );
}

const SALARY_GROUPS = [
  { key: "earnings", title: "Earnings", icon: TrendingUp },
  { key: "employer", title: "Employer Contributions", icon: Landmark },
  { key: "deductions", title: "Deductions", icon: TrendingDown },
];

function SalaryTab({ employee, section, role, onRetry }) {
  const structure = section.data?.structure;
  const calc = section.data?.calculation;
  const breakdown = calc?.salary_breakdown || calc || {};
  const fullPay = normalizeSalaryPayoutMode(calc?.salary_payout_mode) === PAYOUT_FULL_PAY;
  const gross = breakdown.gross_salary ?? calc?.gross_salary ?? calc?.gross;
  const net = fullPay ? gross : (breakdown.net_salary ?? calc?.net_salary ?? calc?.net);
  const components = useMemo(
    () => calc?.components || calc?.breakdown || structure?.components || [],
    [calc, structure]
  );

  const grouped = useMemo(() => {
    const buckets = { earnings: [], deductions: [], employer: [] };
    for (const c of components) {
      const cat = String(c.category || c.type || "").toLowerCase();
      if (fullPay && (cat.includes("deduct") || cat.includes("employer"))) continue;
      if (cat.includes("deduct")) buckets.deductions.push(c);
      else if (cat.includes("employer")) buckets.employer.push(c);
      else buckets.earnings.push(c);
    }
    return buckets;
  }, [components, fullPay]);

  const groups = fullPay ? SALARY_GROUPS.filter((group) => group.key === "earnings") : SALARY_GROUPS;

  return (
    <div className="ep-tab-stack">
      <ProfilePanel
        title="Current Salary Package"
        icon={Wallet}
        tone="indigo"
        actions={
          canManageSalary(role) ? (
            <>
              <Link className="ep-btn ep-btn-ghost ep-btn-sm" to={`/salary/templates/${structure?.template_id || ""}`}>
                View Salary Template
              </Link>
              <Link className="ep-btn ep-btn-primary ep-btn-sm" to={`/employeesalarystructure?employee_id=${employee?.employee_id}`}>
                Change Package
              </Link>
            </>
          ) : null
        }
      >
        <SectionState section={section} onRetry={onRetry} emptyTitle="No salary package">
          {structure ? (
            <ProfileFieldGrid columns={3}>
              <ProfileField icon={ReceiptText} tone="indigo" label="Template" value={structure.template_name || structure.salary_template_name} />
              <ProfileField icon={CalendarDays} tone="blue" label="Effective From" value={formatProfileDate(structure.effective_from)} />
              <ProfileField icon={Repeat} tone="slate" label="Pay Frequency" value={employee?.pay_frequency || "Monthly"} />
              <ProfileField icon={IndianRupee} tone="violet" label="CTC" value={formatCurrencyInr(structure.ctc)} />
              <ProfileField icon={Banknote} tone="teal" label="Gross" value={formatCurrencyInr(gross)} />
              <ProfileField icon={HandCoins} tone="green" label={fullPay ? "Salary payable" : "Net"} value={formatCurrencyInr(net)} />
            </ProfileFieldGrid>
          ) : (
            <ProfileEmptyState title="No salary package assigned" description="Use Employee Salary Structure to assign a template." />
          )}
        </SectionState>
      </ProfilePanel>

      {components.length ? (
        <div className="ep-split-panels ep-split-panels--salary">
          {groups.map(({ key, title, icon }) => (
            <ProfilePanel key={key} title={title} icon={icon} tone="indigo">
              <ul className="ep-component-list">
                {grouped[key].map((c) => (
                  <li key={c.code || c.component_id || c.name}>
                    <span>{c.name || c.component_name}</span>
                    <span>{formatCurrencyInr(c.amount ?? c.monthly_amount ?? c.value)}</span>
                  </li>
                ))}
                {!grouped[key].length ? <li className="ep-muted">No components in this group</li> : null}
              </ul>
            </ProfilePanel>
          ))}
        </div>
      ) : null}

      <ProfilePanel title="Package History" icon={FileClock} tone="indigo" variant="table">
        <SectionState section={section} onRetry={onRetry}>
          {section.data?.history?.length ? (
            <ProfileTableWrap>
              <table className="ep-table">
                <thead>
                  <tr>
                    <th>Effective From</th>
                    <th>Template</th>
                    <th>CTC</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {section.data.history.map((row) => (
                    <tr key={`${row.employee_salary_structure_id || row.id}-${row.effective_from}`}>
                      <td>{formatProfileDate(row.effective_from)}</td>
                      <td>{row.template_name || row.salary_template_name || "—"}</td>
                      <td>{formatCurrencyInr(row.ctc)}</td>
                      <td>{row.is_active === false ? "Historical" : "Active"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ProfileTableWrap>
          ) : (
            <ProfileEmptyState title="No package history" description="Previous salary assignments will be listed here." />
          )}
        </SectionState>
      </ProfilePanel>

      <section className="ep-benefits-block" id="benefits-adjustments" aria-label="Benefits and adjustments">
        <h3 className="ep-section-kicker">Benefits &amp; Adjustments</h3>
        <AdvanceTakenSection employeeId={employee?.employee_id} />
        <FuelReimbursementSection employeeId={employee?.employee_id} role={role} />
      </section>
    </div>
  );
}

function AttendanceTab({ section, onRetry, employeeId }) {
  const s = section.data?.summary;
  return (
    <div className="ep-tab-stack">
      <ProfilePanel
        title="Attendance Summary"
        subtitle="Current month"
        icon={CalendarCheck}
        tone="indigo"
        actions={
          <Link className="ep-btn ep-btn-ghost ep-btn-sm" to="/attendance">
            View Full Attendance
          </Link>
        }
      >
        <SectionState section={section} onRetry={onRetry} emptyTitle="No attendance records">
          {s ? (
            <ProfileFieldGrid columns={3}>
              <ProfileField icon={CalendarDays} tone="blue" label="Working Days" value={s.workingDays} />
              <ProfileField icon={UserCheck} tone="green" label="Present" value={s.present} />
              <ProfileField icon={CalendarX} tone="rose" label="Absent" value={s.absent} />
              <ProfileField icon={Timer} tone="amber" label="Half Day" value={s.halfDay} />
              <ProfileField icon={Palmtree} tone="violet" label="Leave" value={s.leave} />
              <ProfileField icon={Percent} tone="teal" label="Attendance %" value={`${s.attendancePct}%`} />
            </ProfileFieldGrid>
          ) : null}
        </SectionState>
      </ProfilePanel>
      <ProfilePanel
        title="Daily Attendance"
        subtitle={section.data?.from_date ? `${formatProfileDate(section.data.from_date)} – ${formatProfileDate(section.data.to_date)}` : "Current month"}
        icon={CalendarClock}
        tone="indigo"
        variant="table"
      >
        <SectionState section={section} onRetry={onRetry}>
          {section.data?.rows?.length ? (
            <ProfileTableWrap>
              <table className="ep-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Check In</th>
                    <th>Check Out</th>
                    <th>Hours</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {section.data.rows.map((r) => (
                    <tr key={`${employeeId}-${r.date}`}>
                      <td>{r.date}</td>
                      <td>{r.checkIn || "—"}</td>
                      <td>{r.checkOut || "—"}</td>
                      <td>{r.hoursWorked || "—"}</td>
                      <td>{formatStatusLabel(r.status)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ProfileTableWrap>
          ) : (
            <ProfileEmptyState title="No attendance records found for this period." />
          )}
        </SectionState>
      </ProfilePanel>
    </div>
  );
}

function LeaveTab({ section, onRetry, employeeId, role }) {
  return (
    <div className="ep-tab-stack">
      <ProfilePanel
        title="Leave Balances"
        subtitle={`Year ${new Date().getFullYear()}`}
        icon={Palmtree}
        tone="indigo"
        variant="table"
      >
        <SectionState section={section} onRetry={onRetry}>
          {section.data?.balances?.length ? (
            <ProfileTableWrap>
              <table className="ep-table">
                <thead>
                  <tr>
                    <th>Leave Type</th>
                    <th>Entitled</th>
                    <th>Used</th>
                    <th>Pending</th>
                    <th>Available</th>
                  </tr>
                </thead>
                <tbody>
                  {section.data.balances.map((b) => (
                    <tr key={b.balance_id || b.leave_type_id || b.leave_type_name}>
                      <td>{b.leave_type_name || b.name}</td>
                      <td>{formatDays(leaveBalanceEntitled(b))}</td>
                      <td>{formatDays(leaveBalanceUsed(b))}</td>
                      <td>{formatDays(b.pending)}</td>
                      <td>{formatDays(leaveBalanceRemaining(b))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ProfileTableWrap>
          ) : (
            <ProfileEmptyState title="No leave balances" description="Allocate leave policies for this employee to see balances." />
          )}
        </SectionState>
      </ProfilePanel>
      <ProfilePanel
        title="Leave History"
        icon={ClipboardList}
        tone="indigo"
        variant="table"
        // Apply Leave button hidden for now:
        // actions={canEditEmployee(role) ? (
        //   <Link className="ep-btn ep-btn-primary ep-btn-sm" to={`/leave?tab=apply&employee_id=${employeeId}`}>Apply Leave</Link>
        // ) : null}
      >
        <SectionState section={section} onRetry={onRetry}>
          {section.data?.requests?.length ? (
            <ProfileTableWrap>
              <table className="ep-table">
                <thead>
                  <tr>
                    <th>Request</th>
                    <th>Type</th>
                    <th>From</th>
                    <th>To</th>
                    <th>Days</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {section.data.requests.map((r) => {
                    const requestId = r.request_id || r.leave_request_id || r.id;
                    // The leave API has no request code — show a short reference from the request id.
                    const reference =
                      r.request_code || (requestId ? `LR-${String(requestId).slice(0, 8).toUpperCase()}` : "—");
                    return (
                      <tr key={requestId}>
                        <td>
                          <span className="ep-mono" title={requestId ? String(requestId) : undefined}>
                            {reference}
                          </span>
                          {r.applied_at && (
                            <span className="ep-request-applied">Applied {formatProfileDate(r.applied_at)}</span>
                          )}
                        </td>
                        <td>{r.leave_type_name || r.leave_type?.name}</td>
                        <td>{formatProfileDate(r.from_date || r.start_date)}</td>
                        <td>{formatProfileDate(r.to_date || r.end_date)}</td>
                        <td>{r.days ?? r.total_days ?? "—"}</td>
                        <td>{formatStatusLabel(r.status)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </ProfileTableWrap>
          ) : (
            <ProfileEmptyState title="No leave requests" description="Leave applications will appear here." />
          )}
        </SectionState>
      </ProfilePanel>
    </div>
  );
}

function PayrollTab({ employee, section, onRetry }) {
  return (
    <div className="ep-tab-stack">
      <ProfilePanel title="Payroll Summary" icon={IndianRupee} tone="indigo">
        <ProfileFieldGrid columns={2}>
          <ProfileField icon={IndianRupee} tone="violet" label="Current CTC" value={formatCurrencyInr(employee?.annual_ctc)} />
          <ProfileField icon={Repeat} tone="blue" label="Pay Frequency" value={employee?.pay_frequency || "Monthly"} />
        </ProfileFieldGrid>
      </ProfilePanel>
      <ProfilePanel title="Payroll History" icon={FileClock} tone="indigo" variant="table">
        <SectionState section={section} onRetry={onRetry}>
          {section.data?.rows?.length ? (
            <ProfileTableWrap>
              <table className="ep-table">
                <thead>
                  <tr>
                    <th>Period</th>
                    <th>Gross</th>
                    <th>Deductions</th>
                    <th>Net</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {section.data.rows.map((row, idx) => (
                    <tr key={row.payroll_run_id || idx}>
                      <td>{row.pay_period_label || row.period || row.month || "—"}</td>
                      <td>{formatCurrencyInr(row.gross_earnings ?? row.gross)}</td>
                      <td>{formatCurrencyInr(row.total_deductions ?? row.deductions)}</td>
                      <td>{formatCurrencyInr(row.net_pay ?? row.net_salary)}</td>
                      <td>{row.payment_status || row.status || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ProfileTableWrap>
          ) : (
            <ProfileEmptyState
              title="No payroll history"
              description="Processed payroll runs for this employee will appear here."
              action={
                <Link className="ep-btn ep-btn-secondary" to="/reports/employees">
                  Open Employee Salary Register
                </Link>
              }
            />
          )}
        </SectionState>
      </ProfilePanel>
    </div>
  );
}

function StatutoryTab({ section, role, onRetry }) {
  const [showPan, setShowPan] = useState(false);
  const canReveal = canViewStatutorySensitive(role);
  const data = section.data;
  const reveal = (v) => (canReveal ? v : maskSensitive(v));

  const pan = data ? (
    <>
      {canReveal ? maskSensitive(data.pan, { visible: showPan }) : maskSensitive(data.pan)}
      {canReveal && data.pan && !showPan ? (
        <button type="button" className="ep-link-btn" onClick={() => setShowPan(true)}>
          Show
        </button>
      ) : null}
    </>
  ) : null;

  return (
    <div className="ep-tab-stack">
      <ProfilePanel title="Statutory Information" icon={ShieldCheck} tone="indigo">
        <SectionState section={section} onRetry={onRetry}>
          {data ? (
            <ProfileFieldGrid columns={3}>
              <ProfileField icon={ShieldCheck} tone="green" label="PF Applicable" value={data.pf_applicable ? "Yes" : "No"} />
              <ProfileField icon={Fingerprint} tone="indigo" label="UAN" value={reveal(data.uan_number)} mono />
              <ProfileField icon={Hash} tone="blue" label="PF Number" value={reveal(data.pf_number)} mono />
              <ProfileField icon={ShieldCheck} tone="teal" label="ESI Applicable" value={data.esi_applicable ? "Yes" : "No"} />
              <ProfileField icon={Hash} tone="violet" label="ESI Number" value={reveal(data.esic_number)} mono />
              <ProfileField icon={CreditCard} tone="amber" label="PAN" value={pan} mono />
              <ProfileField icon={Scale} tone="slate" label="Tax Regime" value={data.tax_regime} />
              <ProfileField icon={MapPinned} tone="rose" label="Professional Tax State" value={data.pt_state || data.professional_tax_state} />
              <ProfileField icon={ShieldCheck} tone="blue" label="LWF Applicable" value={data.lwf_applicable ? "Yes" : "No"} />
            </ProfileFieldGrid>
          ) : (
            <ProfileEmptyState
              title="Statutory details not configured"
              description="Add PF, ESI, PAN, and other statutory IDs from employee onboarding or statutory setup."
            />
          )}
        </SectionState>
      </ProfilePanel>
    </div>
  );
}

function docStatusClass(status) {
  const key = String(status || "").toLowerCase();
  if (["verified", "approved", "completed"].includes(key)) return "ep-fuel-status ep-fuel-status--approved";
  if (["rejected", "cancelled"].includes(key)) return "ep-fuel-status ep-fuel-status--rejected";
  return "ep-fuel-status ep-fuel-status--pending";
}

function FileLink({ href }) {
  if (!href) return <span className="ep-muted">—</span>;
  return (
    <a className="ep-file-link" href={href} target="_blank" rel="noreferrer">
      <FileText size={14} aria-hidden />
      View
    </a>
  );
}

function DocumentsTab({ section, onRetry }) {
  const documents = section.data?.documents || [];
  const bills = section.data?.bills || [];
  const uploaded = documents.filter((d) => d.document_url);
  const pendingReview = documents.filter((d) => d.document_url && String(d.status).toLowerCase() !== "verified");
  const billFiles = bills.filter((b) => b.attachment);
  // Uploaded files first, then still-required ones.
  const sortedDocs = [...documents].sort((a, b) => Number(Boolean(b.document_url)) - Number(Boolean(a.document_url)));

  return (
    <div className="ep-tab-stack">
      <ProfilePanel
        title="Documents Overview"
        subtitle="Files uploaded by the employee (self-service) and by HR"
        icon={FolderOpen}
        tone="indigo"
      >
        <SectionState section={section} onRetry={onRetry}>
          <ProfileFieldGrid columns={3}>
            <ProfileField icon={FileText} tone="indigo" label="Documents Uploaded" value={`${uploaded.length} of ${documents.length}`} />
            <ProfileField icon={Clock3} tone="amber" label="Awaiting Verification" value={pendingReview.length} />
            <ProfileField icon={ReceiptText} tone="teal" label="Bills & Receipts" value={`${billFiles.length} file${billFiles.length === 1 ? "" : "s"}`} />
          </ProfileFieldGrid>
        </SectionState>
      </ProfilePanel>

      <ProfilePanel title="Employee Documents" subtitle="ID, address, education and employment proofs" icon={FileText} tone="indigo" variant="table">
        <SectionState section={section} onRetry={onRetry}>
          {sortedDocs.length ? (
            <ProfileTableWrap>
              <table className="ep-table">
                <thead>
                  <tr>
                    <th>Document</th>
                    <th>File Name</th>
                    <th>Status</th>
                    <th>Verified On</th>
                    <th>Source</th>
                    <th>File</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedDocs.map((d) => (
                    <tr key={d.document_id}>
                      <td>
                        {formatStatusLabel(d.document_type)}
                        {d.is_mandatory ? <span className="ep-req-tag">Required</span> : null}
                      </td>
                      <td className="ep-cell-clip">{getLifecycleDocumentName(d) || "—"}</td>
                      <td>
                        {d.document_url ? (
                          <span className={docStatusClass(d.status)}>{formatStatusLabel(d.status)}</span>
                        ) : (
                          <span className="ep-muted">Not uploaded</span>
                        )}
                        {d.rejection_reason ? <span className="ep-cell-note">{d.rejection_reason}</span> : null}
                      </td>
                      <td>{d.verified_at ? formatProfileDate(d.verified_at) : "—"}</td>
                      <td>
                        <Link to={`/employee-lifecycle/onboarding/${d.onboarding_id}`}>
                          {d.onboarding_code || "Onboarding"}
                        </Link>
                      </td>
                      <td>
                        <FileLink href={getLifecycleDocumentUrl(d.document_url)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ProfileTableWrap>
          ) : (
            <ProfileEmptyState
              title="No documents on file"
              description="Documents the employee uploads in My Onboarding (self-service) will appear here."
            />
          )}
        </SectionState>
      </ProfilePanel>

      <ProfilePanel title="Bills & Receipts" subtitle="Fuel bills submitted by the employee or HR" icon={ReceiptText} tone="indigo" variant="table">
        <SectionState section={section} onRetry={onRetry}>
          {bills.length ? (
            <ProfileTableWrap>
              <table className="ep-table">
                <thead>
                  <tr>
                    <th>Bill Date</th>
                    <th>Type</th>
                    <th>Bill No.</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Submitted On</th>
                    <th>File</th>
                  </tr>
                </thead>
                <tbody>
                  {bills.map((b) => (
                    <tr key={b.id}>
                      <td>{formatProfileDate(b.bill_date)}</td>
                      <td>Fuel bill</td>
                      <td className="ep-mono">{b.bill_number || "—"}</td>
                      <td>{formatCurrencyInr(b.amount)}</td>
                      <td>
                        <span className={docStatusClass(b.status)}>{formatStatusLabel(String(b.status || "").toLowerCase())}</span>
                      </td>
                      <td>{formatProfileDate(b.created_at)}</td>
                      <td>
                        <FileLink href={b.attachment ? mediaUrl(b.attachment) : null} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ProfileTableWrap>
          ) : (
            <ProfileEmptyState title="No bills submitted" description="Fuel bills with receipts will appear here once submitted." />
          )}
        </SectionState>
      </ProfilePanel>
    </div>
  );
}

function AssetsTab() {
  return (
    <ProfilePanel title="Assigned Assets" icon={Laptop} tone="indigo">
      <ProfileEmptyState
        title="No assets assigned"
        description="Asset assignment is not connected yet. Assign laptops, ID cards, and equipment when the assets module is enabled."
      />
    </ProfilePanel>
  );
}

function LifecycleTab({ employee }) {
  const events = buildLifecycleEvents(employee);
  return (
    <ProfilePanel title="Employee Lifecycle" icon={Route} tone="indigo">
      {events.length ? (
        <ul className="ep-timeline">
          {events.map((ev) => (
            <li key={ev.id}>
              <span className="ep-timeline-date">{formatProfileDate(ev.date)}</span>
              <strong>{ev.title}</strong>
              <span>{ev.description}</span>
            </li>
          ))}
        </ul>
      ) : (
        <ProfileEmptyState title="No lifecycle events" description="Lifecycle milestones will be recorded as they occur." />
      )}
    </ProfilePanel>
  );
}

function ActivityTab({ section, onRetry }) {
  const rows = [
    ...(section.data?.orgAudit || []),
    ...(section.data?.lifecycleAudit || []),
  ].slice(0, 50);

  return (
    <ProfilePanel title="Activity & Audit" subtitle="Latest 50 changes" icon={Activity} tone="indigo">
      <SectionState section={section} onRetry={onRetry}>
        {rows.length ? (
          <ul className="ep-activity-list">
            {rows.map((row, idx) => (
              <li key={row.id || row.audit_id || idx}>
                <div className="ep-activity-head">
                  <strong>{row.action || row.event_type || row.title || "Change"}</strong>
                  <span>{formatProfileDate(row.created_at || row.timestamp || row.date)}</span>
                </div>
                <p>{row.summary || row.description || row.message || "—"}</p>
                {row.actor_name || row.user_name ? (
                  <span className="ep-muted">By {row.actor_name || row.user_name}</span>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <ProfileEmptyState title="No audit activity" description="Changes to this employee will appear in the audit trail." />
        )}
      </SectionState>
    </ProfilePanel>
  );
}

function SettingsTab({ employee, section }) {
  const meta = employee?.metadata || employee?.extra_metadata || {};
  const bank = section.data?.bank;
  return (
    <div className="ep-tab-stack">
      <ProfilePanel title="Employee Access" icon={KeyRound} tone="indigo">
        <ProfileFieldGrid columns={3}>
          <ProfileField icon={KeyRound} tone="green" label="Portal Access" value={employee?.is_active === false ? "Disabled" : "Enabled"} />
          <ProfileField icon={UserCheck} tone="blue" label="Self-service" value={yesNo(meta.self_service_enabled)} />
          <ProfileField icon={Tags} tone="violet" label="Role" value={employee?.role} />
        </ProfileFieldGrid>
      </ProfilePanel>
      <ProfilePanel title="Attendance Settings" icon={CalendarCheck} tone="indigo">
        <ProfileFieldGrid columns={3}>
          <ProfileField icon={CalendarCheck} tone="teal" label="Attendance Tracking" value={yesNo(meta.attendance_enabled)} />
          <ProfileField icon={Clock3} tone="amber" label="Shift" value={meta.shift} />
          <ProfileField icon={Timer} tone="rose" label="Overtime Eligible" value={yesNo(meta.overtime_eligible)} />
        </ProfileFieldGrid>
      </ProfilePanel>
      <ProfilePanel title="Payroll Settings" icon={Settings2} tone="indigo">
        <ProfileFieldGrid columns={4}>
          <ProfileField icon={IndianRupee} tone="violet" label="Payroll Enabled" value={yesNo(meta.payroll_enabled)} />
          <ProfileField icon={ReceiptText} tone="blue" label="Payslip Enabled" value={yesNo(meta.payslip_enabled)} />
          <ProfileField icon={ShieldCheck} tone="green" label="PF Applicability" value={yesNo(meta.pf_applicable)} />
          <ProfileField icon={ShieldCheck} tone="teal" label="ESI Applicability" value={yesNo(meta.esi_applicable)} />
        </ProfileFieldGrid>
      </ProfilePanel>
      <ProfilePanel title="Banking (Verified Account)" icon={Landmark} tone="indigo">
        <SectionState section={section}>
          {bank ? (
            <ProfileFieldGrid columns={3}>
              <ProfileField icon={Landmark} tone="indigo" label="Bank Name" value={bank.bank_name} />
              <ProfileField icon={Hash} tone="slate" label="IFSC" value={bank.ifsc_code} mono />
              <ProfileField icon={BadgeCheck} tone="green" label="Verification" value={bank.verification_status ? formatStatusLabel(bank.verification_status) : "—"} />
            </ProfileFieldGrid>
          ) : (
            <ProfileEmptyState title="No verified bank details" description="Bank information is managed in employee edit / banking." />
          )}
        </SectionState>
      </ProfilePanel>
    </div>
  );
}

export default function EmployeeProfileTabContent({
  activeTab,
  employee,
  sections,
  role,
  orgId,
  onEdit,
  onGoTab,
  onRetrySection,
}) {
  switch (activeTab) {
    case "overview":
      return (
        <OverviewTab employee={employee} sections={sections} role={role} onEdit={onEdit} onGoTab={onGoTab} />
      );
    case "personal":
      return <PersonalTab employee={employee} role={role} onEdit={onEdit} />;
    case "employment":
      return <EmploymentTab employee={employee} />;
    case "organization":
      return (
        <OrganizationTab
          employee={employee}
          section={sections.get("organization")}
          onRetry={() => onRetrySection("organization")}
        />
      );
    case "salary":
      return (
        <SalaryTab
          employee={employee}
          section={sections.get("salary")}
          role={role}
          onRetry={() => onRetrySection("salary")}
        />
      );
    case "attendance":
      return (
        <AttendanceTab
          section={sections.get("attendance")}
          onRetry={() => onRetrySection("attendance")}
          employeeId={employee?.employee_id}
        />
      );
    case "leave":
      return (
        <LeaveTab
          section={sections.get("leave")}
          onRetry={() => onRetrySection("leave")}
          orgId={orgId}
          employeeId={employee?.employee_id}
          role={role}
        />
      );
    case "payroll":
      return (
        <PayrollTab
          employee={employee}
          section={sections.get("payroll")}
          onRetry={() => onRetrySection("payroll")}
        />
      );
    case "statutory":
      return (
        <StatutoryTab
          section={sections.get("statutory")}
          role={role}
          onRetry={() => onRetrySection("statutory")}
        />
      );
    case "documents":
      return <DocumentsTab section={sections.get("documents")} onRetry={() => onRetrySection("documents")} />;
    case "assets":
      return <AssetsTab />;
    case "lifecycle":
      return <LifecycleTab employee={employee} />;
    case "activity":
      return <ActivityTab section={sections.get("activity")} onRetry={() => onRetrySection("activity")} />;
    case "settings":
      return <SettingsTab employee={employee} section={sections.get("settings")} />;
    default:
      return null;
  }
}
